import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";

const QuotationPrintTemplate = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const { data = {}, tableData = [], navigateBack } = location.state || {};
  const decimalCount = useSelector((state) => state.common?.decimalCount) || 2;
  const primaryCurrency =
    useSelector((state) => state.common?.primaryCurrency) || "₹";

  const themeColor = "#000066"; // Exact SMMS quotation theme color

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack || -1);
    },
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 5mm;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handlePrint();
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPhotoUrl = (prod) => {
    let rawImg = prod?.photo || prod?.image || prod?.image_url || "";
    if (
      !rawImg ||
      rawImg === "null" ||
      rawImg === "(NULL)" ||
      rawImg === "undefined"
    ) {
      return null;
    }
    if (typeof rawImg === "object") {
      return rawImg.img || rawImg.url || null;
    }
    if (typeof rawImg === "string") {
      const trimmed = rawImg.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === "object") {
            return parsed.img || parsed.url || null;
          }
        } catch (e) {
          // ignore parsing error
        }
      }
      if (
        trimmed.startsWith("http://") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("data:image")
      ) {
        return trimmed;
      }
      return trimmed;
    }
    return null;
  };

  const customerName =
    data.BillPartyName === "3"
      ? "CASH"
      : data.BillPartyName === "4"
        ? "CASH PURCHASE"
        : data.BillPartyName || data.customer_name || data.party_name || "";
  const customerAddress = data.warranty || data.Address || data.address || "";
  const customerTax =
    data.vat_no || data.TinNo || data.tax_no || data.tax_details || "";
  const customerPhone = data.Ph || data.contact_no || data.phone || "";

  // Summary calculations
  const subTotal =
    data.BillTotalField !== undefined &&
    data.BillTotalField !== null &&
    data.BillTotalField !== ""
      ? Number(data.BillTotalField)
      : tableData.reduce((acc, row) => acc + Number(row.total || 0), 0);

  const discAmt = Number(data.BillDiscAmtField || data.discount_amount || 0);
  const discPerc = data.BillDiscPercField || data.discount_percent || "";
  const gstAmt = Number(
    data.BillPackageField || data.gst || data.other_charges || 0,
  );
  const netTotal =
    data.BillNetTotalField !== undefined &&
    data.BillNetTotalField !== null &&
    data.BillNetTotalField !== ""
      ? Number(data.BillNetTotalField)
      : subTotal - discAmt + gstAmt;

  const openingBalance = Number(data.BalanceAmount || data.balance || 0);
  const newBill = netTotal;
  const cashPart = Number(data.CessP || data.cash_part || 0);
  const chequePart = Number(data.Cess || data.cheque_part || 0);
  const advance = Number(data.advance || 0);
  const closingBalance = openingBalance + newBill - advance;

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return `${primaryCurrency} ${num.toFixed(decimalCount)}`;
  };

  const leftSummaryItems = [];
  if (cashPart > 0) {
    leftSummaryItems.push({
      label: "CASH PART",
      value: formatCurrency(cashPart),
      isRed: true,
    });
  }
  if (chequePart > 0) {
    leftSummaryItems.push({
      label: "CHEQUE PART",
      value: formatCurrency(chequePart),
      isRed: true,
    });
  }
  if (advance > 0) {
    leftSummaryItems.push({
      label: "ADVANCE",
      value: formatCurrency(advance),
      isRed: true,
    });
  }
  leftSummaryItems.push({
    label: "OPENING BALANCE",
    value: formatCurrency(openingBalance),
    isRed: false,
  });
  leftSummaryItems.push({
    label: "NEW BILL",
    value: formatCurrency(newBill),
    isRed: false,
  });
  leftSummaryItems.push({
    label: "CLOSING BALANCE",
    value: formatCurrency(closingBalance),
    isRed: false,
  });

  const rightSummaryItems = [];
  rightSummaryItems.push({
    label: "SUB TOTAL",
    value: formatCurrency(subTotal),
  });
  if (discAmt > 0) {
    rightSummaryItems.push({
      label: discPerc ? `DISCOUNT (${discPerc}%)` : "DISCOUNT",
      value: formatCurrency(discAmt),
    });
  }
  if (gstAmt > 0) {
    rightSummaryItems.push({
      label: "GST",
      value: formatCurrency(gstAmt),
    });
  }
  rightSummaryItems.push({
    label: "NET TOTAL",
    value: formatCurrency(netTotal),
  });

  const maxSummaryRows = Math.max(
    leftSummaryItems.length,
    rightSummaryItems.length,
  );

  // In SMMS, terms only display if the quotation explicitly has a note/terms saved.
  // If no note was added, terms & conditions are not shown.
  const termsCondition =
    data.note ||
    (data.DCBillNo &&
    data.DCBillNo !== "0" &&
    data.DCBillNo !== "NIL" &&
    data.DCBillNo !== '"NIL"'
      ? data.DCBillNo
      : "");

  return (
    <Box sx={{ display: "none" }}>
      {/* Printable Document Container (A4 portrait) */}
      <Box
        ref={componentRef}
        sx={{
          width: "794px",
          minHeight: "1123px",
          bgcolor: "#fff",
          p: "14px 18px",
          boxSizing: "border-box",
          fontFamily: "'DM Sans', sans-serif, Arial",
          color: "#000",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          borderRadius: 1,
          m: "0 auto",
          "@media print": {
            width: "100% !important",
            minHeight: "auto !important",
            height: "auto !important",
            p: "4mm 6mm !important",
            boxSizing: "border-box !important",
            display: "flex !important",
            flexDirection: "column !important",
            justifyContent: "flex-start !important",
            boxShadow: "none !important",
            borderRadius: "0 !important",
            pageBreakAfter: "auto",
          },
        }}
      >
        {/* Top Section: Header, Customer Info, Items Table */}
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header Row: QUOTATION, Order No, and DATE Box */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography
              sx={{
                fontWeight: 900,
                color: themeColor,
                fontSize: "2.2rem",
                lineHeight: 1,
                letterSpacing: "0.5px",
              }}
            >
              QUOTATION
            </Typography>

            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: "#000",
              }}
            >
              ORDER NO: {data.BillNo || data.voucher_no || data.ref_no || ""}
            </Typography>

            {/* Date Box */}
            <Box
              sx={{
                border: "1px solid #000",
                borderRadius: "3px",
                overflow: "hidden",
                textAlign: "center",
                minWidth: 135,
              }}
            >
              <Box
                sx={{
                  py: 0.4,
                  borderBottom: "1px solid #000",
                  bgcolor: themeColor,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  sx={{ letterSpacing: 1, fontSize: "12px", color: "#fff" }}
                >
                  DATE
                </Typography>
              </Box>
              <Box sx={{ py: 0.4, bgcolor: "#fff" }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ fontSize: "13px" }}
                >
                  {dayjs(data.BillDate || new Date()).format("DD-MM-YY")}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Customer Info Banner & Details */}
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                bgcolor: themeColor,
                py: 0.5,
                px: 1.2,
                borderRadius: "3px",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={800}
                sx={{ letterSpacing: 0.8, color: "#fff", fontSize: "13px" }}
              >
                CUSTOMER INFO
              </Typography>
            </Box>

            <Box
              sx={{
                pt: 0.8,
                pb: 0.3,
                px: 0.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                <Typography sx={{ fontSize: "13.5px", color: "#000" }}>
                  <b>NAME:</b> {customerName || ""}
                </Typography>
                <Typography sx={{ fontSize: "13.5px", color: "#000" }}>
                  <b>ADDRESS:</b> {customerAddress || "..."}
                </Typography>
                <Typography sx={{ fontSize: "13.5px", color: "#000" }}>
                  <b>TAX DETAILS:</b> {customerTax || ""}
                </Typography>
              </Box>

              <Box sx={{ pr: 6 }}>
                <Typography sx={{ fontSize: "13.5px", color: "#000" }}>
                  <b>PH:</b> {customerPhone || ""}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Main Table Matching SMMS Layout with Color Code & Model No */}
          <Box sx={{ borderRadius: "3px", overflow: "hidden" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #000",
                fontSize: "13.5px",
                fontFamily: "inherit",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "26%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr
                  style={{
                    backgroundColor: themeColor,
                    color: "#fff",
                    textAlign: "center",
                  }}
                >
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 3px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    SI
                    <br />
                    NO
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 3px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    MODEL NO
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 3px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    COLOR CODE
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 3px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    PHOTO
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 6px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    DESCRIPTION
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 3px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    QTY
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 4px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    UNIT PRICE
                  </th>
                  <th
                    style={{
                      border: "1px solid #000",
                      padding: "6px 4px",
                      fontWeight: 800,
                      fontSize: "13.5px",
                    }}
                  >
                    AMOUNT
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((item, i) => {
                  const subCategory =
                    item.subCategory ||
                    item.sub_category ||
                    item.model_no ||
                    item.ModelNo ||
                    "-";
                  const colorCode =
                    item.code ||
                    item.ItemC ||
                    item.color_code ||
                    item.ColorCode ||
                    "NA";
                  const productName =
                    item.ItemNameTextField ||
                    item.product ||
                    item.productName ||
                    item.Description ||
                    "";
                  const photoUrl = getPhotoUrl(item);
                  const hasPhoto = Boolean(photoUrl);

                  const unitPrice = Number(
                    item.Rate1 || item.rate || item.UnitPrice || 0,
                  ).toFixed(decimalCount);
                  const amount = Number(item.total || item.amount || 0).toFixed(
                    decimalCount,
                  );

                  return (
                    <tr
                      key={i}
                      style={{
                        textAlign: "center",
                        height: hasPhoto ? "84px" : "auto",
                      }}
                    >
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 3px" : "5px 3px",
                          fontWeight: 700,
                          fontSize: "13.5px",
                          verticalAlign: "middle",
                        }}
                      >
                        {i + 1}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 5px" : "5px 5px",
                          fontWeight: 700,
                          fontSize: "12.5px",
                          lineHeight: 1.25,
                          verticalAlign: "middle",
                          wordBreak: "break-word",
                        }}
                      >
                        {subCategory}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 4px" : "5px 4px",
                          fontWeight: 700,
                          fontSize: "12.5px",
                          lineHeight: 1.25,
                          verticalAlign: "middle",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {colorCode}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "3px",
                          verticalAlign: "middle",
                          textAlign: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            maxWidth: hasPhoto ? "115px" : "60px",
                            height: hasPhoto ? "78px" : "36px",
                            bgcolor: "#f4f4f4",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "0 auto",
                            border: "1px solid #e0e0e0",
                            borderRadius: "3px",
                            overflow: "hidden",
                            p: hasPhoto ? "2px" : 0,
                            boxSizing: "border-box",
                          }}
                        >
                          {hasPhoto ? (
                            <img
                              src={photoUrl}
                              alt={productName || "Product"}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#777",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              No Image
                            </Typography>
                          )}
                        </Box>
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 8px" : "5px 6px",
                          fontWeight: 700,
                          textAlign: "left",
                          verticalAlign: "middle",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "13px",
                            color: "#000",
                            lineHeight: 1.35,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {productName}
                        </Typography>
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 3px" : "5px 3px",
                          fontWeight: 700,
                          fontSize: "13.5px",
                          verticalAlign: "middle",
                        }}
                      >
                        {item.qty || item.quantity || 1}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 4px" : "5px 4px",
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "13.5px",
                          verticalAlign: "middle",
                        }}
                      >
                        {unitPrice}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: hasPhoto ? "6px 4px" : "5px 4px",
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "13.5px",
                          verticalAlign: "middle",
                        }}
                      >
                        {amount}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Summary Footer Table attached directly to items table */}
            <table
              style={{
                width: "100%",
                marginTop: "-1px",
                borderCollapse: "collapse",
                border: "1px solid #000",
                fontSize: "13px",
                fontFamily: "inherit",
                tableLayout: "fixed",
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}
            >
              <colgroup>
                <col style={{ width: "36%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "24%" }} />
              </colgroup>
              <tbody>
                {Array.from({ length: maxSummaryRows }).map((_, idx) => {
                  const left = leftSummaryItems[idx] || null;
                  const right = rightSummaryItems[idx] || null;

                  // Dynamically calculate rowSpan so there are never any empty bordered boxes
                  const isLeftLonger =
                    leftSummaryItems.length > rightSummaryItems.length;
                  const rightRowSpan =
                    isLeftLonger && idx === rightSummaryItems.length - 1
                      ? leftSummaryItems.length - rightSummaryItems.length + 1
                      : 1;
                  const skipRight =
                    isLeftLonger && idx >= rightSummaryItems.length;

                  const isRightLonger =
                    rightSummaryItems.length > leftSummaryItems.length;
                  const leftRowSpan =
                    isRightLonger && idx === leftSummaryItems.length - 1
                      ? rightSummaryItems.length - leftSummaryItems.length + 1
                      : 1;
                  const skipLeft =
                    isRightLonger && idx >= leftSummaryItems.length;

                  return (
                    <tr key={idx}>
                      {!skipLeft && left && (
                        <>
                          <td
                            rowSpan={leftRowSpan}
                            style={{
                              border: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              color: left.isRed ? "#c00000" : "inherit",
                            }}
                          >
                            {left.label}
                          </td>
                          <td
                            rowSpan={leftRowSpan}
                            style={{
                              border: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "right",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              color: left.isRed ? "#c00000" : "inherit",
                            }}
                          >
                            {left.value}
                          </td>
                        </>
                      )}

                      {!skipRight && right && (
                        <>
                          <td
                            rowSpan={rightRowSpan}
                            style={{
                              border: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "center",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {right.label}
                          </td>
                          <td
                            rowSpan={rightRowSpan}
                            style={{
                              border: "1px solid #000",
                              padding: "4px 6px",
                              textAlign: "right",
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {right.value}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}

                {/* Terms & Conditions / Note attached directly in the table */}
                {termsCondition && (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        border: "1px solid #000",
                        padding: "8px 12px",
                        textAlign: "left",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          "& .ql-font-serif": { fontFamily: "serif" },
                          "& .ql-align-justify": { textAlign: "justify" },
                          "& .ql-align-center": { textAlign: "center" },
                          "& .ql-align-right": { textAlign: "right" },
                          "& p": {
                            margin: "2px 0",
                            fontSize: "12.5px",
                            lineHeight: 1.4,
                          },
                          "& ul, & ol": {
                            margin: "2px 0",
                            paddingLeft: "20px",
                            fontSize: "12.5px",
                            lineHeight: 1.4,
                          },
                          "& li": { margin: "2px 0" },
                          "& strong": { fontWeight: 800 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: termsCondition,
                        }}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QuotationPrintTemplate;
